import styled from 'styled-components';
import Comment from '@src/components/Molecules/Comment/Comment';
import DonationDetailAside from '@src/components/Organism/Card/DonationDetailAside';
import DonationDetailContent from '@src/components/Organism/Card/DonationDetailContent';
import { useOpenDonationChat } from '@src/hooks/useChat';
import {
  useCommunitySingleMutaion,
  useDonationShareMutaion,
} from '@src/hooks/useCommunitys';
import { devices } from '@src/styles/theme';
import { GetServerSideProps } from 'next';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { community } from '@src/services/api';
import { useSingleCommunity } from '@src/hooks/useCommunitys';
import { useRouter } from 'next/router';
import PageTemplate from '@src/components/Template/Page';
import ChatStartModal from '@src/components/Organism/Modal/ChatStartModal';
import Meta from '@src/components/Meta';
import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { modalAtom } from '@src/recoils/atom/modal';
import useAuth from '@src/hooks/useAuth';
import { metaInfo } from '@src/data/meta';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const { id }: any = ctx.query;
  const isValidId = await community.getDonation(id);

  // TODO : 귀살대한테 말하기 현재 status가 저렇게 오고 있음.
  if (isValidId.status === 404) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
  if (isValidId.status === 410) {
    return {
      redirect: {
        destination: '/community/donation/detail/end-donation',
        permanent: false,
      },
    };
  }
  await queryClient.prefetchQuery(
    ['detail-page', id],
    async () => await community.getDonation(id)
  );
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};

const DetailCommuinity = () => {
  const router = useRouter();
  const { id }: any = router.query;
  const { fetchDonation } = useSingleCommunity(id);
  const { data: community } = fetchDonation();
  // 사이드바 '대화하기'
  const { openChat, isOpening } = useOpenDonationChat();
  // 본문 리액션(응원/공유)
  const { mutate: onLike } = useCommunitySingleMutaion('donation', id);
  const { mutate: onShare } = useDonationShareMutaion(id, true);
  // 디자인: '대화하기'는 확인 모달을 거쳐 방을 연다.
  const [askChat, setAskChat] = useState(false);
  // '돕겠습니다'는 로그인 사용자만 — 비로그인이면 로그인 안내 모달을 띄운다.
  const { fetchProfile } = useAuth();
  const { data: profile } = fetchProfile();
  const setModal = useSetRecoilState(modalAtom);

  /**
   * 신고 — 게시글 신고 API가 없어 운영팀 메일로 연결한다.
   * (가짜 접수 토스트를 띄우지 않는다.)
   */
  const onReport = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    window.location.href = `mailto:help@pple.kr?subject=${encodeURIComponent(
      '[신고] 수혈 요청 게시글'
    )}&body=${encodeURIComponent(`신고할 게시글: ${url}\n\n신고 사유:\n`)}`;
  };

  return (
    <>
      <Meta
        title={community?.title}
        description={community?.content}
        ogImage={metaInfo.donation.ogImage}
      />
      <PageTemplate
        // 기존 Aside(검색·글쓰기·후원 배너) 대신 디자인의 요청자/모집현황 사이드바를 쓴다.
        hideAside
        maxWidth={1120}
        AsideIsListPage={true}
        AsideDisabledBody={true}
        href={'/community/donation'}
        mainText={'헌혈요청'}
      >
        <Breadcrumb>
          <Crumb onClick={() => router.push('/community/board')}>커뮤니티</Crumb>
          <Sep>›</Sep>
          <Crumb onClick={() => router.push('/community/donation')}>
            수혈 요청
          </Crumb>
          <Sep>›</Sep>
          <CrumbCurrent>상세</CrumbCurrent>
        </Breadcrumb>

        <DetailLayout>
          <DetailMain>
            {/* 본문은 디자인 전용 컴포넌트, 댓글은 기존 Comment 재사용 */}
            {community && (
              <DonationDetailContent
                community={community}
                onLike={() => onLike(community.uuid)}
                onShare={() => onShare({ donationUuid: community.uuid })}
                onReport={onReport}
              />
            )}
            <Comment
              replies={community?.replies}
              communityId={community?.uuid}
            />
          </DetailMain>
          {community && (
            <DonationDetailAside
              community={community}
              onChat={() => setAskChat(true)}
              chatDisabled={isOpening}
              canHelp={profile?.status === 'ACTIVE'}
              onHelpBlocked={() =>
                setModal({ id: 'unAuthorized', visible: true })
              }
            />
          )}
        </DetailLayout>

        {askChat && community && (
          <ChatStartModal
            isLoading={isOpening}
            onClose={() => setAskChat(false)}
            stopPropagation={(e) => e.stopPropagation()}
            onConfirm={() => {
              setAskChat(false);
              openChat(community);
            }}
          />
        )}

        {/* 모바일 하단 고정 액션바 (디자인 Blood Request 모바일) */}
        {community && (
          <MobileActionBar>
            <LikeButton
              active={!!community?.liked}
              onClick={() => onLike(community.uuid)}
              aria-label="응원하기"
            >
              {community?.liked ? '♥' : '♡'}
            </LikeButton>
            <MobileChatButton
              onClick={() => setAskChat(true)}
              disabled={isOpening}
            >
              {isOpening ? '채팅방 여는 중…' : '대화하기'}
            </MobileChatButton>
          </MobileActionBar>
        )}
      </PageTemplate>
    </>
  );
};

/** 디자인의 브레드크럼: 커뮤니티 › 수혈 요청 › 상세 */
const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #b7b7b7;
  margin-bottom: 18px;
  @media ${devices.tablet} {
    display: none;
  }
`;
const Crumb = styled.span`
  cursor: pointer;
  &:hover {
    color: #767676;
  }
`;
const CrumbCurrent = styled.span`
  color: #767676;
`;
const Sep = styled.span`
  color: #d8d8d8;
`;

/** 디자인의 상세 2컬럼: 좌 본문(flex:1) + 우 사이드바(328px sticky) */
const DetailLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  @media ${devices.tablet} {
    display: block;
    /* 하단 고정 액션바에 가리지 않도록 */
    padding-bottom: 84px;
  }
`;
const DetailMain = styled.div`
  flex: 1;
  min-width: 0;
`;
const MobileActionBar = styled.div`
  display: none;
  @media ${devices.tablet} {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 40;
    gap: 10px;
    padding: 12px 16px 20px;
    background: #ffffff;
    border-top: 1px solid #f2f2f2;
  }
`;
const LikeButton = styled.button<{ active: boolean }>`
  width: 52px;
  height: 52px;
  flex: none;
  border-radius: 14px;
  font-size: 20px;
  cursor: pointer;
  border: 1px solid ${({ active }) => (active ? '#FF4651' : '#E6E6E6')};
  background: ${({ active }) => (active ? '#FFF1ED' : '#ffffff')};
  color: ${({ active }) => (active ? '#FF4651' : '#767676')};
`;
const MobileChatButton = styled.button`
  flex: 1;
  height: 52px;
  border: none;
  border-radius: 26px;
  background: #ff4651;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    background: #ffa3a3;
  }
`;

export default DetailCommuinity;
